import React, { useEffect, useMemo, useRef, useState } from "react";
import { McPageHeader, McPageFooter } from "../components/McPageLayout";
import "./mcImportEngine.css";
import "./mcConfig.css";
import { buildDefaultState, loadArchivedSessionRecords, loadEngineState, loadSessionRecords } from "../utils/mcEngineData";
import { canAccessAdminTabs, useCurrentMcProfile, loadProfiles } from "../utils/profileAccess";
import { mcGetItem, mcSetItem, mcSetItemSilent, getMcActiveOrgId as mcGetActiveOrgId, getMcCloudSyncState, mcGetSiteResponsibilityAuthorities, mcPatchSiteResponsibility, MC_SYNC_READY_EVENT } from "../utils/mcCloudSync";
import {
  detectSiteHeaders,
  ensureSitesFromHeaders,
  inventoryHeaderFromSiteName,
  loadSites,
  MC_SITE_EVENT,
  MC_SITE_RESPONSIBILITY_BACKUP_KEY,
  normalizeSiteId,
  saveSites,
} from "../utils/mcOrgData";


const ORDER_SHEETS_KEY = "mc-morpheus-order-sheets";
const MC_RETAINED_BILLING_SITES_KEY = "mc-retained-billing-sites";
const MC_SITE_RESPONSIBILITY_PENDING_KEY = "__mc_site_responsibility_outbox_v2";
const RESPONSIBILITY_FIELDS = ["siteManagerPersonId", "operationsManagerPersonId", "ownerPersonId"];

function safeJson(raw, fallback) {
  try { return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
}

function siteMatchKey(value = "") {
  return normalizeSiteId(value || "");
}

function aliasesForSite(site = {}) {
  return Array.from(new Set([
    site.siteId,
    site.name,
    site.storeKey,
    site.csvKey,
    site.inventoryKey,
    ...(Array.isArray(site.aliases) ? site.aliases : []),
  ].filter(Boolean).map(siteMatchKey).filter(Boolean)));
}

function recordAliases(row = {}) {
  return Array.from(new Set([
    row.siteId,
    row.storeKey,
    row.store,
    row.storeName,
    row.siteName,
    row.scope,
    row.mapping?.systemSoh,
    ...(Array.isArray(row.sites) ? row.sites : []),
  ].filter(Boolean).map(siteMatchKey).filter(Boolean)));
}

function recordBelongsToSite(row = {}, site = {}) {
  const siteAliases = aliasesForSite(site);
  const rowAliases = recordAliases(row);
  return siteAliases.some((alias) => rowAliases.includes(alias));
}

function siteHasBillingPeriodUsage(site = {}) {
  const sessions = loadSessionRecords();
  const archives = loadArchivedSessionRecords();
  const mos = safeJson(mcGetItem(ORDER_SHEETS_KEY), []);
  const orderSheets = Array.isArray(mos) ? mos.map((sheet) => ({ ...sheet, store: sheet.storeName, site: sheet.storeName })) : [];
  return [...sessions, ...archives, ...orderSheets].some((row) => recordBelongsToSite(row, site));
}

function loadRetainedBillingSites() {
  const parsed = safeJson(mcGetItem(MC_RETAINED_BILLING_SITES_KEY), []);
  return Array.isArray(parsed) ? parsed : [];
}

function saveRetainedBillingSite(site = {}) {
  if (!site?.siteId) return;
  const now = new Date().toISOString();
  const retained = loadRetainedBillingSites();
  const nextSite = {
    ...site,
    active: false,
    removed: true,
    billingRetained: true,
    billingEnabled: true,
    removedAt: site.removedAt || now,
    updatedAt: now,
  };
  const byId = new Map(retained.map((item) => [item.siteId || normalizeSiteId(item.name || item.storeKey), item]));
  byId.set(nextSite.siteId, nextSite);
  mcSetItem(MC_RETAINED_BILLING_SITES_KEY, JSON.stringify(Array.from(byId.values())));
}


function readPendingResponsibilityWrite(orgId = "") {
  if (typeof window === "undefined" || !orgId) return null;
  try {
    const parsed = JSON.parse(window.localStorage?.getItem(MC_SITE_RESPONSIBILITY_PENDING_KEY) || "null");
    return parsed?.orgId === orgId && parsed.changes && typeof parsed.changes === "object" ? parsed : null;
  } catch (_) { return null; }
}

function writePendingResponsibilityWrite(payload) {
  if (typeof window === "undefined") return;
  try {
    if (!payload || !Object.keys(payload.changes || {}).length) window.localStorage?.removeItem(MC_SITE_RESPONSIBILITY_PENDING_KEY);
    else window.localStorage?.setItem(MC_SITE_RESPONSIBILITY_PENDING_KEY, JSON.stringify(payload));
  } catch (error) { console.error("Site responsibility outbox could not be saved", error); }
}

function overlayPendingResponsibilities(sites = [], orgId = "") {
  const pending = readPendingResponsibilityWrite(orgId);
  if (!pending) return sites;
  return sites.map((site) => ({ ...site, ...(pending.changes[site.siteId] || {}) }));
}

function writeLocalResponsibilityAuthorities(responsibility, sites) {
  if (typeof responsibility === "string") mcSetItemSilent(MC_SITE_RESPONSIBILITY_BACKUP_KEY, responsibility);
  if (typeof sites === "string") mcSetItemSilent("mc-sites", sites);
}

function blankSite() {
  return { name: "", storeKey: "", active: true, siteManagerPersonId: "", operationsManagerPersonId: "", ownerPersonId: "" };
}


function profileName(profile = {}) {
  return [profile.name, profile.surname].filter(Boolean).join(" ").trim() || profile.email || "Person";
}
function profileOperationalRole(profile = {}) {
  return profile.operationalRole || (String(profile.accessLevel || "").toLowerCase() === "owner" ? "Owner" : "Stock Controller");
}
function eligibleForAssignment(profile = {}, assignment = "siteManager") {
  if (profile.active === false) return false;
  const role = profileOperationalRole(profile).toLowerCase();
  const accessLevel = String(profile.accessLevel || "").trim().toLowerCase();
  const isAdmin = ["owner", "administrator"].includes(accessLevel) || profile.isPrimaryOwner === true;
  if (assignment === "owner") return profile.isPrimaryOwner === true || accessLevel === "owner" || role === "owner";
  if (assignment === "operationsManager") return isAdmin || ["owner", "operations manager"].includes(role);
  // Site response ownership is an operational routing choice. Admin/owner profiles
  // must remain selectable even when an older Directory record has no explicit
  // operationalRole yet. Explicit operational roles continue to be preferred.
  return isAdmin || ["owner", "operations manager", "site manager", "supervisor"].includes(role);
}
function AssignmentSearch({ people, value, label, assignment, onChange }) {
  const selected = people.find((person) => person.id === value);
  const selectedName = selected ? profileName(selected) : "";
  const [query, setQuery] = useState(selectedName);

  // Profiles hydrate independently from the site registry. On refresh the saved
  // person id can arrive before the People directory; repaint the human-readable
  // name as soon as that directory becomes available instead of leaving a valid
  // assignment looking blank.
  useEffect(() => {
    if (selectedName) setQuery(selectedName);
    else if (!value) setQuery("");
  }, [value, selectedName]);

  const suggestions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term || selected && term === selectedName.toLowerCase()) return [];
    return people
      .filter((person) => eligibleForAssignment(person, assignment))
      .filter((person) => `${profileName(person)} ${person.email || ""} ${profileOperationalRole(person)}`.toLowerCase().includes(term))
      .slice(0, 8);
  }, [people, query, selected?.id, selectedName, assignment]);

  return <div className="mc-assignmentSearch">
    <label>{label}</label>
    <input
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      onFocus={() => {
        // If the field was displaying a saved person, keep that value until the
        // operator actually chooses a replacement or presses Clear. Merely typing
        // a search must never persist an accidental unassignment.
      }}
      placeholder={`Search ${label.toLowerCase()}...`}
    />
    {suggestions.length ? <div className="mc-assignmentSearch__menu">{suggestions.map((person) => <button key={person.id} type="button" onClick={() => { onChange(person.id); setQuery(profileName(person)); }}><strong>{profileName(person)}</strong><span>{profileOperationalRole(person)} · {person.email || "No email"}</span></button>)}</div> : null}
    {selected ? <button type="button" className="mc-assignmentSearch__clear" onClick={() => { onChange(""); setQuery(""); }}>Clear</button> : null}
  </div>;
}

export default function MorpheusCountSites() {
  const currentProfile = useCurrentMcProfile();
  const canManage = canAccessAdminTabs(currentProfile);
  const engine = loadEngineState() || buildDefaultState();
  const detectedHeaders = useMemo(() => detectSiteHeaders(engine?.parsedHeaders || []), [engine?.parsedHeaders]);
  const [sites, setSites] = useState(() => loadSites());
  const [form, setForm] = useState(blankSite());
  const [people, setPeople] = useState(() => loadProfiles());
  const [message, setMessage] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const responsibilityPersistRef = useRef({ inFlight: false });
  const routeLeavingRef = useRef(false);
  const startingLocalSitesRef = useRef(sites);
  const lastResponsibilityRefreshRef = useRef({ orgId: "", at: 0 });
  const [responsibilityStatus, setResponsibilityStatus] = useState("Checking shared site assignments…");
  const [recoverableAssignments, setRecoverableAssignments] = useState([]);

  const refreshResponsibilityFromCloud = async ({ force = false } = {}) => {
    const orgId = mcGetActiveOrgId();
    if (!orgId || !getMcCloudSyncState()?.dataHydrated) return false;
    // Cloud-ready may fire repeatedly during operational writes. A Sites visit
    // needs a fresh authority read, not a repeated read storm.
    const last = lastResponsibilityRefreshRef.current;
    if (!force && last.orgId === orgId && Date.now() - last.at < 15000) return true;
    lastResponsibilityRefreshRef.current = { orgId, at: Date.now() };
    try {
      const remote = await mcGetSiteResponsibilityAuthorities();
      if (mcGetActiveOrgId() !== remote.orgId || routeLeavingRef.current) return false;
      const remoteSites = safeJson(remote.sites, []);
      const remoteBackup = safeJson(remote.responsibility, {});
      // Do not erase the last known correct local assignments when an old org
      // has not yet created the dedicated responsibility document.
      const remoteSiteRows = Array.isArray(remoteSites) ? remoteSites : [];
      const remoteBackupRows = remoteBackup && typeof remoteBackup === "object" && !Array.isArray(remoteBackup) ? remoteBackup : {};
      const recoverable = [];
      startingLocalSitesRef.current.forEach((site) => {
        const matching = remoteSiteRows.find((row) => row.siteId === site.siteId) || {};
        const saved = remoteBackupRows[site.siteId] || matching;
        RESPONSIBILITY_FIELDS.forEach((field) => {
          if (site[field] && !saved[field]) recoverable.push({ siteId: site.siteId, siteName: site.name, field, personId: site[field] });
        });
      });
      setRecoverableAssignments(recoverable);
      if (remote.sites !== null) mcSetItemSilent("mc-sites", remote.sites);
      if (remote.responsibility !== null) mcSetItemSilent(MC_SITE_RESPONSIBILITY_BACKUP_KEY, remote.responsibility);
      if (detectedHeaders.length) ensureSitesFromHeaders(engine?.parsedHeaders || []);
      setSites(overlayPendingResponsibilities(loadSites(), orgId));
      setResponsibilityStatus(recoverable.length
        ? `${recoverable.length} assignment(s) exist only on this device. Review and publish them to share WhatsApp recipients.`
        : "Shared assignments loaded from Firestore.");
      return true;
    } catch (error) {
      lastResponsibilityRefreshRef.current = { orgId: "", at: 0 };
      if (!routeLeavingRef.current) {
        setResponsibilityStatus(`Could not verify shared assignments: ${error?.message || error}. Check your connection and retry.`);
      }
      return false;
    }
  };

  const drainPendingResponsibilityWrite = async () => {
    const state = responsibilityPersistRef.current;
    if (state.inFlight) return;
    const orgId = mcGetActiveOrgId();
    if (!orgId || !getMcCloudSyncState()?.dataHydrated) return;
    state.inFlight = true;
    try {
      while (mcGetActiveOrgId() === orgId) {
        const pending = readPendingResponsibilityWrite(orgId);
        const firstSite = Object.entries(pending?.changes || {}).find(([, patch]) => Object.keys(patch || {}).length);
        if (!firstSite) break;
        const [siteId, patch] = firstSite;
        const field = RESPONSIBILITY_FIELDS.find((key) => Object.prototype.hasOwnProperty.call(patch, key));
        if (!field) break;
        const value = patch[field] || "";
        let confirmed;
        try {
          confirmed = await mcPatchSiteResponsibility(siteId, field, value, orgId);
        } catch (error) {
          if (!routeLeavingRef.current) {
            setResponsibilityStatus(`Assignment not yet saved to Firestore: ${error?.message || error}. Retry when online.`);
          }
          console.error("Site responsibility cloud save failed", { siteId, field, orgId, error });
          break;
        }
        if (mcGetActiveOrgId() !== orgId) break;
        const latest = readPendingResponsibilityWrite(orgId);
        const latestChanges = { ...(latest?.changes || {}) };
        const latestPatch = { ...(latestChanges[siteId] || {}) };
        if (Object.prototype.hasOwnProperty.call(latestPatch, field) && latestPatch[field] === value) {
          delete latestPatch[field];
          if (Object.keys(latestPatch).length) latestChanges[siteId] = latestPatch;
          else delete latestChanges[siteId];
          writePendingResponsibilityWrite({ orgId, changes: latestChanges });
        }
        // Apply the transaction's merged server result, then overlay any newer
        // edits made while that transaction was in flight.
        writeLocalResponsibilityAuthorities(confirmed.responsibility, confirmed.sites);
        if (!routeLeavingRef.current) {
          setSites(overlayPendingResponsibilities(loadSites(), orgId));
          const remaining = Object.values(readPendingResponsibilityWrite(orgId)?.changes || {}).reduce((total, fields) => total + Object.keys(fields).length, 0);
          setResponsibilityStatus(remaining ? `Saving ${remaining} assignment(s) to Firestore…` : "Recipients saved and confirmed in Firestore.");
          setRecoverableAssignments((current) => current.filter((item) => !(item.siteId === siteId && item.field === field)));
        }
      }
    } finally {
      state.inFlight = false;
    }
  };

  useEffect(() => {
    routeLeavingRef.current = false;
    // Do not reconcile/import sites until Firestore has supplied the org's
    // responsibility ledger. An early site discovery write could overwrite it.
    const sync = () => {
      if (!routeLeavingRef.current) setSites(overlayPendingResponsibilities(loadSites(), mcGetActiveOrgId()));
    };
    const syncPeople = () => { if (!routeLeavingRef.current) setPeople(loadProfiles()); };
    const onCloudReady = () => {
      if (routeLeavingRef.current) return;
      syncPeople();
      refreshResponsibilityFromCloud().then(() => drainPendingResponsibilityWrite()).catch(() => {});
    };
    const onFocus = () => {
      if (routeLeavingRef.current || document.visibilityState === "hidden") return;
      refreshResponsibilityFromCloud().then(() => drainPendingResponsibilityWrite()).catch(() => {});
    };
    const onRouteLeaving = () => { routeLeavingRef.current = true; };
    window.addEventListener(MC_SITE_EVENT, sync);
    window.addEventListener("mc-profile-change", syncPeople);
    window.addEventListener("mc-cloud-sync", syncPeople);
    window.addEventListener(MC_SYNC_READY_EVENT, onCloudReady);
    window.addEventListener("focus", onFocus);
    window.addEventListener("mc-tab-navigate-start", onRouteLeaving);
    sync();
    syncPeople();
    onCloudReady();
    return () => {
      routeLeavingRef.current = true;
      window.removeEventListener(MC_SITE_EVENT, sync);
      window.removeEventListener("mc-profile-change", syncPeople);
      window.removeEventListener("mc-cloud-sync", syncPeople);
      window.removeEventListener(MC_SYNC_READY_EVENT, onCloudReady);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("mc-tab-navigate-start", onRouteLeaving);
    };
  }, [detectedHeaders.join("|")]);

  function persistResponsibility(siteId, patch) {
    const orgId = mcGetActiveOrgId();
    if (!orgId || !getMcCloudSyncState()?.dataHydrated) {
      setResponsibilityStatus("Morpheus is still syncing. Wait for shared assignments to load before editing.");
      return;
    }
    const previous = readPendingResponsibilityWrite(orgId);
    const changes = { ...(previous?.changes || {}) };
    changes[siteId] = { ...(changes[siteId] || {}), ...patch };
    writePendingResponsibilityWrite({ orgId, changes });
    // Update only the chosen role in this browser. Never replace all other
    // assignees from the currently displayed (possibly incomplete) site list.
    const next = sites.map((site) => site.siteId === siteId ? { ...site, ...patch } : site);
    const existingBackup = safeJson(mcGetItem(MC_SITE_RESPONSIBILITY_BACKUP_KEY), {});
    const backup = existingBackup && typeof existingBackup === "object" && !Array.isArray(existingBackup) ? { ...existingBackup } : {};
    backup[siteId] = { ...(backup[siteId] || {}), ...patch, updatedAt: new Date().toISOString() };
    mcSetItemSilent(MC_SITE_RESPONSIBILITY_BACKUP_KEY, JSON.stringify(backup));
    mcSetItemSilent("mc-sites", JSON.stringify(next));
    setSites(next);
    setResponsibilityStatus("Saving assignment to Firestore…");
    drainPendingResponsibilityWrite().catch((error) => {
      console.error("Unable to drain site responsibility outbox", error);
    });
  }

  function persist(nextSites, note = "Sites saved.") {
    // Non-assignment site actions must not rebuild/replace the dedicated role
    // ledger from this browser's potentially stale site rows.
    const saved = saveSites(nextSites);
    setSites(saved);
    setMessage(note);
  }

  async function recoverLocalResponsibilities() {
    const toRecover = [...recoverableAssignments];
    if (!toRecover.length) return;
    const preview = toRecover.map(({ siteName, field, personId }) => {
      const role = field === "siteManagerPersonId" ? "Site Manager" : field === "operationsManagerPersonId" ? "Operations Manager" : "Owner";
      const selected = people.find((person) => person.id === personId);
      return `${siteName} · ${role}: ${selected ? profileName(selected) : `Unknown profile (${personId})`}`;
    });
    if (!window.confirm(`Publish ${toRecover.length} local recipient assignment(s) to this organisation?\n\n${preview.join("\n")}\n\nOnly proceed if these are the intended WhatsApp recipients.`)) return;
    const orgId = mcGetActiveOrgId();
    if (!orgId || !getMcCloudSyncState()?.dataHydrated) return;
    const pending = readPendingResponsibilityWrite(orgId);
    const changes = { ...(pending?.changes || {}) };
    toRecover.forEach(({ siteId, field, personId }) => {
      changes[siteId] = { ...(changes[siteId] || {}), [field]: personId };
    });
    writePendingResponsibilityWrite({ orgId, changes });
    setResponsibilityStatus(`Publishing ${toRecover.length} local recipient assignment(s)…`);
    await drainPendingResponsibilityWrite();
  }

  function updateSite(siteId, patch) {
    const patchKeys = Object.keys(patch || {});
    const responsibilityOnly = patchKeys.length > 0 && patchKeys.every((key) => [
      "siteManagerPersonId",
      "operationsManagerPersonId",
      "ownerPersonId",
    ].includes(key));
    if (responsibilityOnly) {
      persistResponsibility(siteId, patch);
      return;
    }

    const next = sites.map((site) => {
      if (site.siteId !== siteId) return site;
      const name = patch.name !== undefined ? patch.name : site.name;
      const storeKey = patch.storeKey !== undefined ? patch.storeKey : site.storeKey;
      return {
        ...site,
        ...patch,
        siteId: site.siteId || normalizeSiteId(name || storeKey),
        name,
        storeKey: storeKey || inventoryHeaderFromSiteName(name),
        aliases: Array.from(new Set([...(site.aliases || []), storeKey || site.storeKey, name || site.name].filter(Boolean))),
      };
    });
    persist(next, "Site updated.");
  }

  function confirmRemoveSite() {
    if (!deleteTarget?.siteId) return;
    const shouldRetainBilling = siteHasBillingPeriodUsage(deleteTarget);
    if (shouldRetainBilling) saveRetainedBillingSite(deleteTarget);
    const next = sites.filter((site) => site.siteId !== deleteTarget.siteId);
    persist(
      next,
      shouldRetainBilling
        ? `${deleteTarget.name || "Site"} removed from active MC sites. Billing history is retained for this period.`
        : `${deleteTarget.name || "Site"} removed from MC sites.`
    );
    setDeleteTarget(null);
  }


  function addSite(e) {
    e.preventDefault();
    const name = String(form.name || "").trim();
    if (!name) return setMessage("Enter a site name first.");
    const storeKey = form.storeKey || inventoryHeaderFromSiteName(name);
    const siteId = normalizeSiteId(name || storeKey);
    if (sites.some((site) => site.siteId === siteId)) return setMessage("That site already exists.");
    persist([
      ...sites,
      {
        siteId,
        name,
        storeKey,
        active: form.active !== false,
        billingEnabled: true,
        aliases: Array.from(new Set([name, storeKey].filter(Boolean))),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ], "Site created.");
    setForm(blankSite());
  }

  if (!canManage) {
    return (
      <div className="mc-enginePage mc-sitesPage mc-tabPage">
        <div className="mc-enginePage__grid" />
        <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
        <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />

        <McPageHeader activeKey="sites" subtitle="Only admin can manage MC sites." />
        <McPageFooter />
      </div>
    );
  }

  return (
    <div className="mc-enginePage mc-sitesPage mc-tabPage">
      <div className="mc-enginePage__grid" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />

      <McPageHeader activeKey="sites" />

      <section className="mc-enginePage__heroCards">
        <article className="mc-engineCard"><span>Total Sites</span><strong>{sites.length}</strong></article>
        <article className="mc-engineCard"><span>Active Sites</span><strong>{sites.filter((site) => site.active !== false).length}</strong></article>
        <article className="mc-engineCard"><span>Detected CSV Site Columns</span><strong>{detectedHeaders.length}</strong></article>
      </section>

      <section className="mc-enginePage__content">
        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header">
            <div><h3>Create site</h3><p>Add a site manually when it is not present in the latest CSV.</p></div>
            <div className="mc-engineBadge">Site Registry</div>
          </div>
          <form className="mc-fieldGrid" onSubmit={addSite}>
            <div className="mc-fieldControl"><label>Site Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, storeKey: inventoryHeaderFromSiteName(e.target.value) })} placeholder="Example: Kuils River" /></div>
            <div className="mc-fieldControl"><label>CSV Inventory Key</label><input value={form.storeKey} onChange={(e) => setForm({ ...form, storeKey: e.target.value })} placeholder="inventory_Kuils_River" /></div>
            <div className="mc-inlineActions" style={{ marginTop: 14 }}><button className="mc-engineBtn mc-engineBtn--primary" type="submit">Create Site</button></div>
          </form>
          {message ? <div className="mc-helperText" style={{ marginTop: 12 }}>{message}</div> : null}
        </article>

        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header">
            <div><h3>Site response responsibility</h3><p>Sites only decide who starts and owns the response. Channels and escalation timing are configured under People → Escalation.</p></div>
            <div className="mc-engineBadge">{sites.length} Sites</div>
          </div>
          <div className="mc-helperText" style={{ marginBottom: 12 }} role="status">
            {responsibilityStatus}{" "}
            <button className="mc-engineBtn" type="button" onClick={() => refreshResponsibilityFromCloud({ force: true }).then(() => drainPendingResponsibilityWrite())}>Refresh shared assignments</button>
            {recoverableAssignments.length ? <button className="mc-engineBtn mc-engineBtn--primary" type="button" onClick={recoverLocalResponsibilities}>Publish {recoverableAssignments.length} local assignment(s)</button> : null}
          </div>
          {sites.length ? (
            <div className="mc-liveRuleTable">
              <div className="mc-liveRuleTable__head" style={{ gridTemplateColumns: "1fr 1.15fr 1.25fr 1.25fr 1.25fr .5fr .42fr" }}><div>Site</div><div>CSV Key</div><div>Site Manager</div><div>Operations Manager</div><div>Owner</div><div className="mc-liveRuleTable__headAction">Status</div><div className="mc-liveRuleTable__headAction">Remove</div></div>
              {sites.map((site) => (
                <div className="mc-liveRuleTable__row" style={{ gridTemplateColumns: "1fr 1.15fr 1.25fr 1.25fr 1.25fr .5fr .42fr" }} key={site.siteId}>
                  <div><input value={site.name || ""} onChange={(e) => updateSite(site.siteId, { name: e.target.value })} /></div>
                  <div><input value={site.storeKey || ""} onChange={(e) => updateSite(site.siteId, { storeKey: e.target.value })} /></div>
                  <AssignmentSearch people={people} value={site.siteManagerPersonId} label="Site Manager" assignment="siteManager" onChange={(value) => updateSite(site.siteId, { siteManagerPersonId: value })} />
                  <AssignmentSearch people={people} value={site.operationsManagerPersonId} label="Operations Manager" assignment="operationsManager" onChange={(value) => updateSite(site.siteId, { operationsManagerPersonId: value })} />
                  <AssignmentSearch people={people} value={site.ownerPersonId} label="Owner" assignment="owner" onChange={(value) => updateSite(site.siteId, { ownerPersonId: value })} />
                  <div className="mc-liveRuleAction">
                    <button type="button" className={`mc-configToggle ${site.active !== false ? "is-on" : ""}`} onClick={() => updateSite(site.siteId, { active: site.active === false })}>
                      <span className="mc-configToggle__track"><span className="mc-configToggle__knob" /></span><strong>{site.active !== false ? "Active" : "Inactive"}</strong>
                    </button>
                  </div>
                  <div className="mc-liveRuleAction">
                    <button
                      type="button"
                      className="mc-engineBtn mc-siteRemovePill"
                      onClick={() => setDeleteTarget(site)}
                      style={{ borderColor: "rgba(255, 83, 117, 0.45)", color: "#ff8fa3" }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mc-emptyState"><strong>No sites created yet.</strong><p>Upload a CSV with inventory_ columns, or create your first site manually.</p></div>
          )}
        </article>
      </section>

      {deleteTarget ? (
        <div
          className="mc-modalBackdrop"
          role="presentation"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 80,
            display: "grid",
            placeItems: "center",
            padding: 24,
            background: "rgba(1, 12, 24, 0.58)",
            backdropFilter: "blur(16px)",
          }}
        >
          <div
            className="mc-confirmModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-site-title"
            style={{
              width: "min(520px, 100%)",
              border: "1px solid rgba(255, 83, 117, 0.34)",
              borderRadius: 28,
              padding: 28,
              background: "linear-gradient(145deg, rgba(7, 22, 42, 0.96), rgba(10, 35, 58, 0.92))",
              boxShadow: "0 28px 80px rgba(0, 0, 0, 0.38), 0 0 46px rgba(255, 83, 117, 0.16)",
              color: "#f7fbff",
            }}
          >
            <div className="mc-enginePage__eyebrow">Confirm remove site</div>
            <h3 id="remove-site-title">Remove {deleteTarget.name || "this site"}?</h3>
            <p>
              This removes the site from the MC site registry and dropdowns for this organisation.
              Existing count history is left untouched. If this site was already billable in the current billing period, it will still remain visible in Billing until that period is closed.
            </p>
            <div className="mc-inlineActions" style={{ justifyContent: "flex-end", marginTop: 18 }}>
              <button type="button" className="mc-engineBtn" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button
                type="button"
                className="mc-engineBtn mc-engineBtn--primary"
                onClick={confirmRemoveSite}
                style={{ background: "linear-gradient(135deg, #ff5b7b, #ff8a3d)", boxShadow: "0 18px 40px rgba(255, 91, 123, 0.25)" }}
              >
                Remove Site
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <McPageFooter />
    </div>
  );
}
